// @ts-nocheck
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Flag, Trash2, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import MentionText from './MentionText';
import { parseMentions, saveMentions } from '@/hooks/useMentionSearch';

interface Comment {
  id: string;
  post_id: string;
  profile_id: string;
  content: string;
  created_at: string;
  profiles: {
    id: string;
    name: string | null;
    profile_photo_url: string | null;
    username: string | null;
  };
}

interface PostCommentsProps {
  postId: string;
  postAuthorId: string;
  currentUserId?: string;
  currentUserAvatar?: string | null;
  currentUserName?: string | null;
}

const COMMENTS_LIMIT = 10;

const PostComments: React.FC<PostCommentsProps> = ({
  postId,
  postAuthorId,
  currentUserId,
  currentUserAvatar,
  currentUserName,
}) => {
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState('');
  const [offset, setOffset] = useState(0);
  const [deleteCommentId, setDeleteCommentId] = useState<string | null>(null);
  const [reportCommentId, setReportCommentId] = useState<string | null>(null);

  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Fetch comments for this post
  const { data: commentsData, isLoading } = useQuery({
    queryKey: ['wall_post_comments', postId, offset],
    queryFn: async () => {
      const { data, error, count } = await supabase
        .from('wall_post_comments')
        .select(`
          *,
          profiles:profile_id (
            id,
            name,
            profile_photo_url,
            username
          )
        `, { count: 'exact' })
        .eq('post_id', postId)
        .eq('is_hidden', false)
        .order('created_at', { ascending: true })
        .range(0, offset + COMMENTS_LIMIT - 1);

      if (error) throw error;

      return { comments: data as Comment[], total: count || 0 };
    },
  });

  const comments = commentsData?.comments || [];
  const totalComments = commentsData?.total || 0;
  const hasMore = comments.length < totalComments;

  // Add comment mutation
  const addCommentMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!currentUserId) throw new Error('You must be logged in to comment');

      const { data: newComment, error } = await supabase
        .from('wall_post_comments')
        .insert({
          post_id: postId,
          profile_id: currentUserId,
          content,
        })
        .select()
        .single();

      if (error) throw error;

      // Update comments_count on the post
      const { data: postData } = await supabase
        .from('wall_posts')
        .select('comments_count')
        .eq('id', postId)
        .single();

      if (postData) {
        await supabase
          .from('wall_posts')
          .update({ comments_count: (postData.comments_count || 0) + 1 })
          .eq('id', postId);
      }

      // Create notification for post author (unless commenting on own post)
      if (postAuthorId && postAuthorId !== currentUserId) {
        await supabase
          .from('wall_notifications')
          .insert({
            recipient_id: postAuthorId,
            actor_id: currentUserId,
            post_id: postId,
            comment_id: newComment.id,
            type: 'comment',
          });
      }

      return newComment;
    },
    onSuccess: async (newComment, content) => {
      // Save mentions for the comment
      if (currentUserId) {
        const mentionedUsernames = parseMentions(content);
        if (mentionedUsernames.length > 0) {
          await saveMentions(mentionedUsernames, currentUserId, undefined, newComment.id);
        }
      }
      
      setNewComment('');
      queryClient.invalidateQueries({ queryKey: ['wall_post_comments', postId] });
      queryClient.invalidateQueries({ queryKey: ['wall_posts'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add comment');
    },
  });

  // Delete comment mutation
  const deleteCommentMutation = useMutation({
    mutationFn: async (commentId: string) => {
      const { error } = await supabase
        .from('wall_post_comments')
        .delete()
        .eq('id', commentId)
        .eq('profile_id', currentUserId);

      if (error) throw error;

      // Update comments_count on the post
      const { data: postData } = await supabase
        .from('wall_posts')
        .select('comments_count')
        .eq('id', postId)
        .single();

      if (postData) {
        await supabase
          .from('wall_posts')
          .update({ comments_count: Math.max(0, (postData.comments_count || 0) - 1) })
          .eq('id', postId);
      }
    },
    onSuccess: () => {
      setDeleteCommentId(null);
      toast.success('Comment deleted');
      queryClient.invalidateQueries({ queryKey: ['wall_post_comments', postId] });
      queryClient.invalidateQueries({ queryKey: ['wall_posts'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete comment');
    },
  });

  // Report comment mutation
  const reportCommentMutation = useMutation({
    mutationFn: async (commentId: string) => {
      if (!currentUserId) throw new Error('You must be logged in to report');

      const { error } = await supabase
        .from('wall_post_reports')
        .insert({
          post_id: postId,
          comment_id: commentId,
          reported_by: currentUserId,
          reason: 'inappropriate_comment',
        });

      if (error) throw error;
    },
    onSuccess: () => {
      setReportCommentId(null);
      toast.success('Comment reported. Thank you for helping keep our community safe.');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to report comment');
    },
  });

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    addCommentMutation.mutate(newComment.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment(e);
    }
  };

  return (
    <div className="pt-3 border-t">
      {/* Comments List */}
      <div className="space-y-3 mb-3">
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : comments.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-2">
            No comments yet. Be the first to comment!
          </p>
        ) : (
          <>
            {comments.map((comment) => (
              <div key={comment.id} className="flex gap-2 group">
                <Link to={`/profile/${comment.profiles?.username || comment.profile_id}`}>
                  <Avatar className="h-8 w-8">
                    <AvatarImage
                      src={comment.profiles?.profile_photo_url || undefined}
                      alt={comment.profiles?.name || 'User'}
                    />
                    <AvatarFallback className="bg-muted text-muted-foreground text-xs">
                      {getInitials(comment.profiles?.name)}
                    </AvatarFallback>
                  </Avatar>
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="bg-muted rounded-lg px-3 py-2">
                    <Link
                      to={`/profile/${comment.profiles?.username || comment.profile_id}`}
                      className="font-medium text-sm hover:underline"
                    >
                      {comment.profiles?.name || 'Unknown User'}
                    </Link>
                    <p className="text-sm whitespace-pre-wrap break-words">
                      <MentionText content={comment.content} />
                    </p>
                  </div>
                  <div className="flex items-center gap-3 mt-1 px-1">
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                    </span>
                    {currentUserId && currentUserId !== comment.profile_id && (
                      <button
                        onClick={() => setReportCommentId(comment.id)}
                        className="text-xs text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1"
                      >
                        <Flag className="h-3 w-3" />
                        Report
                      </button>
                    )}
                    {currentUserId === comment.profile_id && (
                      <button
                        onClick={() => setDeleteCommentId(comment.id)}
                        className="text-xs text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1"
                      >
                        <Trash2 className="h-3 w-3" />
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {hasMore && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOffset(prev => prev + COMMENTS_LIMIT)}
                className="w-full text-muted-foreground"
              >
                Load more comments
              </Button>
            )}
          </>
        )}
      </div>

      {/* Add Comment Input */}
      {currentUserId && (
        <form onSubmit={handleSubmitComment} className="flex gap-2">
          <Avatar className="h-8 w-8">
            <AvatarImage src={currentUserAvatar || undefined} alt={currentUserName || 'You'} />
            <AvatarFallback className="bg-primary text-primary-foreground text-xs">
              {getInitials(currentUserName)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 flex gap-2">
            <Input
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Add a comment..."
              className="flex-1"
              disabled={addCommentMutation.isPending}
            />
            <Button
              type="submit"
              size="icon"
              disabled={!newComment.trim() || addCommentMutation.isPending}
            >
              {addCommentMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </form>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteCommentId} onOpenChange={() => setDeleteCommentId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Comment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this comment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteCommentId && deleteCommentMutation.mutate(deleteCommentId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Report Confirmation Dialog */}
      <AlertDialog open={!!reportCommentId} onOpenChange={() => setReportCommentId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Report Comment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to report this comment? This will be reviewed by our team.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => reportCommentId && reportCommentMutation.mutate(reportCommentId)}
            >
              Report
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PostComments;
